import React from 'react'
import Select, { components as reactSelectComponents } from 'react-select'

/**
 * `<Select>` wrapper that stamps testids: options as
 * `<testId|select>-option-<label-slug>`, plus the control (`testId`) and input
 * (`inputId`). Ref forwards to `<Select>` for RHF's shouldFocusError.
 * Keep `optionSlug` in sync with tests/e2e/helpers/select.js.
 */
export const optionSlug = (label) =>
  String(label)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '')

// Module-scope: react/no-unstable-nested-components forbids defining these in
// render. Testids ride on selectProps.
const TestidOption = (props) => (
  <reactSelectComponents.Option
    {...props}
    innerProps={{
      ...props.innerProps,
      'data-testid': `${
        props.selectProps.optionTestIdPrefix || 'select'
      }-option-${optionSlug(props.data.label)}`,
    }}
  />
)

const TestidControl = (props) => (
  <reactSelectComponents.Control
    {...props}
    innerProps={{
      ...props.innerProps,
      'data-testid': props.selectProps.controlTestId,
    }}
  />
)

const TestidInput = (props) => (
  <reactSelectComponents.Input
    {...props}
    data-testid={props.selectProps.inputTestId}
  />
)

const LoxSelect = React.forwardRef(
  ({ testId, inputId, components, ...props }, ref) => {
    const merged = React.useMemo(() => {
      const parts = { Option: TestidOption }
      if (testId) parts.Control = TestidControl
      if (inputId) parts.Input = TestidInput
      return { ...parts, ...components }
    }, [testId, inputId, components])

    return (
      <Select
        {...props}
        ref={ref}
        inputId={inputId}
        optionTestIdPrefix={testId}
        controlTestId={testId ? `${testId}-control` : undefined}
        inputTestId={inputId}
        components={merged}
      />
    )
  }
)

LoxSelect.displayName = 'LoxSelect'

export default LoxSelect
